import { toMap } from '@Common/Helpers/CollectionUtils';
import {
    DBMapping,
    DBMappingEmbededDataSource,
    DerivedMetric,
    DimensionItem,
    MetricGroupDocument,
    MetricPool,
    Process,
    ProcessMetric,
    RecordDocument,
    RepositoryDocument,
    RModel,
    someOfTypes,
    TranslationTable
} from '@InstanceFactory';
import dbMapperControlHelper from '@Studio/Controls/DBMapping/DBMappingHelper';
import { MetricsInStatementCollector, RFunctionsInStatementCollector } from '@Studio/StatementVisitor';

export class DocumentBuilder {
    constructor() {}

    public getDocumentReferences(document): any[] {
        const result = [];
        switch (document.$type) {
            case RecordDocument.typeName:
                result.addRange(this.getMappingReferences(document.dbMapping));
                break;
            case RModel.typeName:
                result.addRange(this.getMappingReferences(document.dbMapping));
                break;
            case TranslationTable.typeName:
                result.addRange(this.getMappingReferences(document.dbMapping));
                break;
            case MetricPool.typeName:
                result.addRange(this.getMappingReferences(document.template.dbMapping));
                break;
            case ProcessMetric.typeName:
                result.push(document.process);
                break;
            case DerivedMetric.typeName:
                const visitor = new MetricsInStatementCollector();
                visitor.accept(document.statement);
                result.addRange(_.map(visitor.metricOperands, mo => mo.metric));
                break;
            case Process.typeName:
                result.addRange(
                    _(document._states)
                        .map((s: any) => s._record)
                        .filter(r => r)
                        .value()
                );
                break;
            case MetricGroupDocument.typeName:
                result.addRange(_.map(document.metrics, (s: any) => s.metric));
                break;
        }
        return result;
    }

    private getMappingReferences(dbMapping: DBMapping): any[] {
        const result = [];
        dbMapperControlHelper
            .getAllSources(dbMapping)
            .filter(s => !DBMappingEmbededDataSource.isInstanceOfType(s))
            .forEach((source: any) => {
                result.push(source.sourceDocument);
                if (source.dataSource) {
                    result.push(source.dataSource);
                }

                dbMapperControlHelper.getSourceStatements(source).forEach(st => {
                    const visitor = new RFunctionsInStatementCollector();
                    visitor.accept(st.statement);
                    visitor.rFunctionOperands.forEach(op => result.push(op.rFunction.function));
                });
            });
        return result;
    }

    public fillReferences(documents: any[], docsByReference: { document; reference }[]) {
        const docsMap = _.keyBy<{ document; reference }>(docsByReference, ({ reference }) => {
            return reference.id;
        });
        _(documents).forEach(doc => {
            const references = this.getDocumentReferences(doc);
            _(references).forEach(ref => {
                if (docsMap[ref.id]) {
                    ref.document = docsMap[ref.id].document;
                }
                if (ref.document) {
                    ref.name = ref.document.name;
                }
            });
        });
    }

    public updateReferencesToInsightObjects(document: RepositoryDocument): void {
        const withItems = someOfTypes(RecordDocument, RModel, TranslationTable, ProcessMetric, DerivedMetric);
        if (withItems(document)) {
            this.updateItemsFilter(document.items);
        }
        if (MetricPool.isInstanceOfType(document)) {
            this.updateItemsFilter(document.template.items);
        }
    }

    private readonly filtersMap = _.once(() => {
        const { filters } = altosoft.model?.insightMeta ?? {};
        if (filters) {
            return toMap(filters);
        } else {
            return null;
        }
    });

    private updateItemsFilter(items: DimensionItem[]): void {
        const filtersMap = this.filtersMap();
        if (filtersMap === null) {
            return;
        }
        for (const { filter } of items) {
            if (filter) {
                filter.name = filtersMap.get(filter.id)?.name ?? filter.name;
            }
        }
    }
}
